import React, { type ReactNode, useContext, useMemo } from 'react';

import {
  Box,
  ButtonGroup,
  Container,
  type ContainerProps,
  Flex,
  type FlexProps,
  HStack,
  Stack,
} from '@chakra-ui/react';
import useMeasure from 'react-use-measure';

import {
  useCoachLayoutContext,
  useCoachLayoutHideFooter,
  useCoachLayoutHideNav,
} from './CoachLayout';
import { COACH_NAV_BAR_HEIGHT } from './constants';

type CoachLayoutPageContextValue = {
  nav: React.ReactNode;
  noContainer: boolean;
  containerMaxWidth: ContainerProps['maxW'];
};

const CoachLayoutPageContext =
  React.createContext<CoachLayoutPageContextValue | null>(null);

const useCoachLayoutPageContext = () => {
  const context = useContext(CoachLayoutPageContext);
  if (context === null) {
    throw new Error('Missing parent <CoachLayoutPage> component');
  }
  return context;
};

const PageContainer = ({ children, maxW, ...rest }: ContainerProps) => {
  const { noContainer, containerMaxWidth } = useCoachLayoutPageContext();

  if (noContainer) return <>{children}</>;
  return (
    <Container
      display="flex"
      flexDirection="column"
      flex="1"
      w="full"
      p={0}
      maxW={maxW ?? containerMaxWidth}
      {...rest}
    >
      {children}
    </Container>
  );
};

type CoachLayoutPageTopBarProps = FlexProps & {
  leftActions?: ReactNode;
  rightActions?: ReactNode;
  isFixed?: boolean;
  isConfirmDiscardChanges?: boolean;
  containerMaxWidth?: ContainerProps['maxW'];
};

export const CoachLayoutPageTopBar = ({
  children,
  leftActions,
  rightActions,
  isFixed = true,
  containerMaxWidth,
  ...rest
}: CoachLayoutPageTopBarProps) => {
  const { navDisplayed } = useCoachLayoutContext();
  const [ref, { height }] = useMeasure();

  return (
    <>
      {isFixed && <Box h={`${height}px`} />}
      <Flex
        zIndex={2}
        direction="column"
        py={3}
        boxShadow="layout"
        bg="white"
        ref={ref}
        borderBottom="1px solid transparent"
        borderBottomColor="gray.100"
        {...(isFixed
          ? {
              top: navDisplayed ? COACH_NAV_BAR_HEIGHT : '0',
              position: 'fixed',
              right: '0',
              left: '0',
            }
          : {})}
        {...rest}
      >
        <Box w="full" h="0" pb="safe-top" />
        <PageContainer maxW={containerMaxWidth}>
          <HStack spacing="4">
            {!!leftActions && (
              <ButtonGroup size="sm" spacing={3}>
                {leftActions}
              </ButtonGroup>
            )}
            <Box flex="1">{children}</Box>
            {!!rightActions && (
              <ButtonGroup size="sm" spacing={3}>
                {rightActions}
              </ButtonGroup>
            )}
          </HStack>
        </PageContainer>
      </Flex>
    </>
  );
};

type CoachLayoutPageContentProps = FlexProps & {
  containerMaxWidth?: ContainerProps['maxW'];
  navContainerProps?: FlexProps;
};

export const CoachLayoutPageContent = ({
  children,
  containerMaxWidth,
  navContainerProps,
  ...rest
}: CoachLayoutPageContentProps) => {
  const { nav } = useCoachLayoutPageContext();
  return (
    <Flex position="relative" zIndex="1" direction="column" flex="1" {...rest}>
      <PageContainer maxW={containerMaxWidth} pb={2}>
        <Stack
          direction={{ base: 'column', lg: 'row' }}
          spacing={{ base: 0, lg: '4' }}
          flex="1"
        >
          {nav && (
            <Flex
              direction="column"
              minW="0"
              w={{ base: 'full', lg: '20rem' }}
              {...navContainerProps}
            >
              {nav}
            </Flex>
          )}
          <Flex direction="column" flex="1" minW="0">
            {children}
          </Flex>
        </Stack>
      </PageContainer>
      <Box w="full" h="0" pb="safe-bottom" />
    </Flex>
  );
};

type CoachLayoutPageBottomBarProps = FlexProps & {
  containerMaxWidth?: ContainerProps['maxW'];
};

export const CoachLayoutPageBottomBar = ({
  children,
  containerMaxWidth,
  ...rest
}: CoachLayoutPageBottomBarProps) => {
  const [ref, { height }] = useMeasure();

  return (
    <>
      <Box h={`${height}px`} />
      <Flex
        zIndex="3"
        ref={ref}
        direction="column"
        mt="auto"
        position="fixed"
        bottom="0"
        insetStart="0"
        insetEnd="0"
        py="2"
        boxShadow="layout"
        bg="white"
        borderTop="1px solid transparent"
        {...rest}
      >
        <PageContainer maxW={containerMaxWidth}>{children}</PageContainer>
        <Box w="full" h="0" pb="safe-bottom" />
      </Flex>
    </>
  );
};

type CoachLayoutPageProps = FlexProps & {
  showNavBar?: boolean;
  showFooter?: boolean;
  containerMaxWidth?: ContainerProps['maxW'];
  noContainer?: boolean;
  nav?: React.ReactNode;
};

export const CoachLayoutPage = ({
  showNavBar = true,
  showFooter = true,
  noContainer = false,
  containerMaxWidth = 'container.xl',
  nav = null,
  ...rest
}: CoachLayoutPageProps) => {
  useCoachLayoutHideNav(showNavBar);
  useCoachLayoutHideFooter(showFooter);

  const value = useMemo(
    () => ({
      nav,
      noContainer,
      containerMaxWidth,
    }),
    [containerMaxWidth, noContainer, nav]
  );

  return (
    <CoachLayoutPageContext.Provider value={value}>
      <Flex direction="column" flex="1" position="relative" {...rest} />
    </CoachLayoutPageContext.Provider>
  );
};
